import React, { Fragment } from 'react';
import PropTypes, { bool, string } from 'prop-types';
import classnames from 'classnames';
import { Field } from 'react-final-form';
import { triggerEditPageChangesMadeAnalytics } from '~/analytics/analytics.listener';
import { FormFieldAdapter } from '~/components';
import { toNonBreakableString } from '~/utils';
import { OpenInNewOutlined } from 'jl-design-system/components/icons/OpenInNewOutlined';

import styles from './Question.scss';

// DS v33 floating-label types — the label is rendered inside the input by the DS component,
// so we hide the external QuestionTitle to avoid a duplicate label.
// Values match OCAB_QUESTION_TYPE in helpers/builder.js.
const FLOATING_LABEL_TYPES = [
  'TEXT',
  'EMAIL',
  'TEL',
  'TEXT_FIELD',
  'SELECT',
  'DROPDOWN',
  'DROPDOWN_PRICE',
  'DATE_INPUT',
];

// Compose validators to work with react-final-form
const composeValidators = (validators) => {
  if (!validators) return undefined;
  if (typeof validators === 'function') return validators;
  if (!Array.isArray(validators)) return undefined;

  // Filter out non-function values
  const validatorFunctions = validators.filter((v) => typeof v === 'function');

  if (validatorFunctions.length === 0) return undefined;
  if (validatorFunctions.length === 1) return validatorFunctions[0];

  // Compose multiple validators
  return (value) => {
    for (const validator of validatorFunctions) {
      const error = validator(value);
      if (error) return error;
    }
    return undefined;
  };
};

const buildQuestionDescription = (description) => {
  const linkRegexp = new RegExp(/\[(.+?) link=(https:\/\/www\.johnlewis\.com\/.+?)\]/gm);
  const textAndLinks = [];
  let match = linkRegexp.exec(description);
  let cursor = 0;
  while (match !== null) {
    textAndLinks.push(
      <Fragment key={`text_before_link_${cursor}`}>
        {description.substring(cursor, match.index)}
      </Fragment>,
    );
    textAndLinks.push(
      <Fragment key={`link_${cursor}`}>
        <a href={match[2]} target="_blank" rel="noopener noreferrer">
          {toNonBreakableString(match[1])}
          <OpenInNewOutlined className={styles.linkIcon} size="medium" />
        </a>
      </Fragment>,
    );

    cursor = match.index + match[0].length;
    match = linkRegexp.exec(description);
  }
  textAndLinks.push(
    <Fragment key={`text_after_link_${cursor}`}>
      {description.substring(cursor, description.length)}
    </Fragment>,
  );
  return <Fragment>{textAndLinks}</Fragment>;
};

const resolveTitle = (question) => question.title || question.label;
const getDataTest = (question) => resolveTitle(question).replace(/ /g, '_');

export class Question extends React.PureComponent {
  handleChange = () => {
    const { editMode, sectionTitle } = this.props;

    if (editMode) {
      triggerEditPageChangesMadeAnalytics('questions', `questions:${sectionTitle.toLowerCase()}`);
    }
  };

  render() {
    const { question, modClassName, formSectionName } = this.props;

    const {
      label,
      title,
      optional,
      reduxFieldConfig,
      description,
      instruction,
      questionTypeClassName,
      questionClassName,
      ...formFieldConfig
    } = question;

    const { name, validate, parse, format, ...otherReduxFieldConfig } = reduxFieldConfig || {};

    // Prefix field name with formSectionName if provided
    const fieldName = formSectionName ? `${formSectionName}.${name}` : name;

    // Append "(optional)" to title if the field is optional
    const displayTitle = title && optional ? `${title} (optional)` : title;
    // Append "(optional)" to label if the field is optional and has a label
    const displayLabel = label && optional ? `${label} (optional)` : label;

    // DS v33 floating-label components render the label inside the input
    const hasFloatingLabel = FLOATING_LABEL_TYPES.includes(formFieldConfig.type);

    return (
      <div className={styles.QuestionContainer}>
        {displayTitle && !hasFloatingLabel && (
          <div className={styles.QuestionTitle}>{displayTitle}</div>
        )}
        {description && !hasFloatingLabel && (
          <div className={styles.QuestionDescription}>{buildQuestionDescription(description)}</div>
        )}
        <div
          className={classnames(
            styles.Question,
            modClassName,
            questionTypeClassName,
            questionClassName,
          )}
          data-testid={getDataTest(question)}
        >
          <Field
            name={fieldName}
            type="radio"
            validate={composeValidators(validate)}
            parse={parse}
            format={format}
            {...otherReduxFieldConfig}
          >
            {({ input, meta }) => (
              <Fragment>
                <FormFieldAdapter
                  {...input}
                  meta={meta}
                  error={(meta.touched || meta.submitFailed) && meta.error}
                  onChange={(e) => {
                    input.onChange(e);
                    this.handleChange();
                  }}
                  formFieldConfig={{
                    ...formFieldConfig,
                    label: displayTitle,
                    checkboxText: displayLabel || displayTitle,
                    required: !optional,
                    defaultValue:
                      formFieldConfig.defaultValue === null
                        ? undefined
                        : formFieldConfig.defaultValue,
                    instruction,
                    ...(hasFloatingLabel && description ? { supportingText: description } : {}),
                  }}
                />
                {(meta.touched || meta.submitFailed) && meta.error && (
                  <div className={styles.QuestionError}>{meta.error}</div>
                )}
              </Fragment>
            )}
          </Field>
        </div>
      </div>
    );
  }
}

Question.propTypes = {
  question: PropTypes.shape({
    title: PropTypes.string,
    label: PropTypes.string,
    description: PropTypes.string,
    optional: PropTypes.bool,
    component: PropTypes.func.isRequired,
    questionClassName: PropTypes.string,
    reduxFieldConfig: PropTypes.shape({}).isRequired,
  }).isRequired,

  autoFocus: PropTypes.bool,
  modClassName: PropTypes.string,
  editMode: bool,
  sectionTitle: string,
  formSectionName: PropTypes.string,
};

Question.defaultProps = {
  modClassName: undefined,
  autoFocus: false,
  sectionTitle: '',
  editMode: false,
  formSectionName: '',
};
